import type { ReactNode } from 'react';

/* ── Sparkline SVG ──────────────────────────────────────────────── */

function SparklineSvg({ data, color }: { data: readonly number[]; color: string }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const padY = 3;
  const padX = 1;

  const points = data
    .map((val, i) => {
      const x = padX + (i / (data.length - 1)) * (100 - padX * 2);
      const y = 36 - padY - ((val - min) / range) * (36 - padY * 2);
      return `${String(x)},${String(y)}`;
    })
    .join(' ');

  const lastX = padX + ((data.length - 1) / (data.length - 1)) * (100 - padX * 2);

  return (
    <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 36">
      <polygon
        points={`${String(padX)},${String(36 - padY)} ${points} ${String(lastX)},${String(36 - padY)}`}
        fill={color}
        opacity={0.08}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function SparklineSection({
  data,
  color,
  label,
  rangeLabel,
}: {
  data: readonly number[];
  color: string;
  label: string;
  rangeLabel?: string | undefined;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1000
        ? `${(n / 1000).toFixed(0)}K`
        : n >= 100
          ? String(Math.round(n))
          : n.toFixed(1);

  return (
    <div className="mt-auto pt-2 w-full">
      <div className="relative w-full h-[36px]">
        <SparklineSvg data={data} color={color} />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-edge" />
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[9px] text-ink-faint">
          {rangeLabel ?? `${String(data.length)}w ago`}
        </span>
        <span className="text-[9px] text-ink-muted font-medium">
          {label} · {fmt(min)}–{fmt(max)}
        </span>
        <span className="text-[9px] text-ink-faint">today</span>
      </div>
    </div>
  );
}

/* ── Mini Donut ─────────────────────────────────────────────────── */

interface DonutSegment {
  readonly value: number;
  readonly color: string;
  readonly label: string;
}

export function MiniDonut({
  segments,
  size = 48,
}: {
  segments: readonly DonutSegment[];
  size?: number;
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  const r = 18;
  const circumference = 2 * Math.PI * r;
  const center = size / 2;
  let offset = 0;

  return (
    <div className="flex items-center gap-2">
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`}>
        {segments.map((seg) => {
          const pct = seg.value / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const currentOffset = offset;
          offset += dash;
          return (
            <circle
              key={seg.label}
              cx={center}
              cy={center}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={5}
              strokeDasharray={`${String(dash)} ${String(gap)}`}
              strokeDashoffset={-currentOffset}
              transform={`rotate(-90 ${String(center)} ${String(center)})`}
            />
          );
        })}
      </svg>
      <div className="flex flex-col gap-0.5">
        {segments
          .filter((seg) => seg.value > 0)
          .map((seg) => (
            <div key={seg.label} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: seg.color }}
              />
              <span className="text-[10px] text-ink-muted whitespace-nowrap">
                {seg.value} {seg.label}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ── Stacked Bar ────────────────────────────────────────────────── */

export function StackedBar({
  segments,
  height = 18,
}: {
  segments: readonly DonutSegment[];
  height?: number;
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  return (
    <div className="flex items-center gap-2 w-full">
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height: `${String(height)}px` }}
      >
        {segments
          .filter((seg) => seg.value > 0)
          .map((seg) => {
            const pct = (seg.value / total) * 100;
            return (
              <div
                key={seg.label}
                className="flex items-center justify-center text-[9px] font-semibold text-white leading-none"
                style={{
                  backgroundColor: seg.color,
                  width: `${pct.toFixed(1)}%`,
                  minWidth: pct > 5 ? undefined : '14px',
                }}
                title={`${seg.label}: ${String(seg.value)} (${pct.toFixed(0)}%)`}
              >
                {pct >= 15 ? `${String(seg.value)} ${seg.label}` : ''}
              </div>
            );
          })}
      </div>
    </div>
  );
}

/* ── Severity Bar ───────────────────────────────────────────────── */

interface SeverityCount {
  readonly high: number;
  readonly medium: number;
  readonly low: number;
}

export function SeverityBar({ counts }: { counts: SeverityCount }) {
  const total = counts.high + counts.medium + counts.low;
  if (total === 0) return null;

  const segments = [
    { value: counts.high, color: 'bg-danger', label: 'high', dotColor: 'bg-danger' },
    { value: counts.medium, color: 'bg-caution', label: 'medium', dotColor: 'bg-caution' },
    { value: counts.low, color: 'bg-ink-faint', label: 'low', dotColor: 'bg-ink-faint' },
  ];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        {segments.map(
          (seg) =>
            seg.value > 0 && (
              <div
                key={seg.label}
                className={`h-full ${seg.color}`}
                style={{ width: `${String((seg.value / total) * 100)}%` }}
              />
            ),
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        {segments
          .filter((seg) => seg.value > 0)
          .map((seg) => (
            <div key={seg.label} className="flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${seg.dotColor}`} />
              <span className="text-[10px] text-ink-muted whitespace-nowrap">
                {seg.value} {seg.label}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ── Margin Badge ───────────────────────────────────────────────── */

export function MarginBadge({ margin }: { margin: number }) {
  const tone =
    margin >= 20
      ? 'bg-profit/10 text-profit'
      : margin >= 0
        ? 'bg-caution/10 text-caution'
        : 'bg-danger/10 text-danger';

  return (
    <div className={`flex flex-col items-center rounded-lg px-2.5 py-1.5 ${tone}`}>
      <span className="text-sm font-bold">{margin.toFixed(1)}%</span>
      <span className="text-[9px] font-medium uppercase tracking-wide opacity-70">margin</span>
    </div>
  );
}

/* ── Trend Indicator ────────────────────────────────────────────── */

export interface TrendData {
  readonly pct: string;
  readonly direction: 'up' | 'down' | 'flat';
  readonly favorable: boolean;
}

export function TrendIndicator({
  trend,
  context,
}: {
  trend: TrendData;
  context?: string | undefined;
}) {
  if (trend.direction === 'flat') return null;

  const arrow = trend.direction === 'up' ? '↑' : '↓';
  const color = trend.favorable ? 'text-profit' : 'text-danger';

  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-xs font-bold ${color}`}>
        {arrow} {trend.pct}
      </span>
      {context !== undefined && <span className="text-[9px] text-ink-faint">{context}</span>}
    </span>
  );
}

/* ── Mini Bar Chart (for concentration) ─────────────────────────── */

export function MiniBarChart({
  bars,
  height = 36,
  formatValue,
}: {
  bars: readonly { readonly label: string; readonly value: number }[];
  height?: number;
  formatValue?: ((v: number) => string) | undefined;
}) {
  if (bars.length === 0) return null;
  const max = Math.max(...bars.map((b) => b.value), 1);
  const barMaxPx = height - 4;
  const fmt = formatValue ?? ((v: number) => v.toFixed(0));

  return (
    <div className="flex items-end gap-0.5 w-full" style={{ height: `${String(height)}px` }}>
      {bars.map((bar) => (
        <div key={bar.label} className="flex flex-col items-center gap-0.5 flex-1">
          <div
            className="w-full min-w-[6px] rounded-t-sm bg-profit/40"
            style={{ height: `${String(Math.max(2, (bar.value / max) * barMaxPx))}px` }}
            title={`${bar.label}: ${fmt(bar.value)}`}
          />
        </div>
      ))}
    </div>
  );
}

/* ── KpiCard (main) ─────────────────────────────────────────────── */

type Tone = 'profit' | 'revenue' | 'caution' | 'danger' | 'neutral';

const borderColor: Record<Tone, string> = {
  profit: 'border-l-profit',
  revenue: 'border-l-revenue',
  caution: 'border-l-caution',
  danger: 'border-l-danger',
  neutral: 'border-l-ink-faint',
};

const valueColor: Record<Tone, string> = {
  profit: 'text-profit',
  revenue: 'text-revenue',
  caution: 'text-caution',
  danger: 'text-danger',
  neutral: 'text-ink',
};

export function KpiCard({
  label,
  value,
  exact,
  subtitle,
  tone = 'neutral',
  delay = 0,
  trend,
  trendContext,
  sparkline,
  sparklineColor,
  sparklineLabel,
  sparklineRangeLabel,
  footer,
  children,
}: {
  label: string;
  value: string;
  exact?: string | undefined;
  subtitle?: string | undefined;
  tone?: Tone | undefined;
  delay?: number | undefined;
  trend?: TrendData | undefined;
  trendContext?: string | undefined;
  sparkline?: readonly number[] | undefined;
  sparklineColor?: string | undefined;
  sparklineLabel?: string | undefined;
  sparklineRangeLabel?: string | undefined;
  footer?: ReactNode | undefined;
  children?: ReactNode | undefined;
}) {
  const hasSparkline =
    sparkline !== undefined && sparkline.length >= 2 && sparklineColor !== undefined;

  return (
    <section
      className={`rise flex min-h-[140px] flex-col justify-between rounded-xl border border-edge border-l-4 bg-surface-raised p-3 shadow-sm ${borderColor[tone]}`}
      style={{ animationDelay: `${String(delay)}ms` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            {label}
          </p>
          <p
            className={`figure mt-1 text-2xl font-bold leading-tight ${valueColor[tone]}`}
            title={exact}
          >
            {value}
          </p>
          <div className="mt-1 flex flex-col gap-0.5">
            {subtitle !== undefined && <p className="text-[10px] text-ink-faint">{subtitle}</p>}
            {trend !== undefined && <TrendIndicator trend={trend} context={trendContext} />}
          </div>
        </div>
        {children !== undefined && <div className="shrink-0">{children}</div>}
      </div>

      {hasSparkline && (
        <SparklineSection
          data={sparkline}
          color={sparklineColor}
          label={sparklineLabel ?? ''}
          rangeLabel={sparklineRangeLabel}
        />
      )}
      {footer !== undefined && <div className="mt-auto pt-2 w-full">{footer}</div>}
    </section>
  );
}

/* ── Utility: compute trend delta ───────────────────────────────── */

export function computeTrend(current: number, prior: number, upIsFavorable = true): TrendData {
  if (prior === 0) return { pct: '—', direction: 'flat', favorable: true };
  const change = ((current - prior) / prior) * 100;
  const direction: TrendData['direction'] = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const favorable =
    direction === 'flat' || (upIsFavorable ? direction === 'up' : direction === 'down');
  return { pct: `${Math.abs(change).toFixed(1)}%`, direction, favorable };
}
